"use client"

import {
  APP_SHORTCUT_COMMANDS,
  SHORTCUT_COMMAND_DEFINITIONS,
  SHORTCUT_COMMANDS_BY_ID,
  type AppShortcutCommandId,
  type ShortcutCommandDefinition,
} from "@/lib/shortcuts/commands"
import {
  doesShortcutMatch,
  formatShortcutLabel,
  getShortcutPlatform,
  isEventTargetEditable,
  type ShortcutPlatform,
} from "@/lib/shortcuts/keys"
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react"
import { usePathname, useRouter } from "next/navigation"

import { KeyboardShortcutsModal } from "@/app/components/common/keyboard-shortcuts-modal"
import { useStore } from "@/app/components/common/store.provider"
import type { PermissionAction, PermissionResource, UserRole } from "@/lib/types/auth.types"
import { getProfilePath } from "@/lib/utils/profile-path"
import { observer } from "mobx-react-lite"

interface ShortcutContextValue {
  executeCommand: (commandId: AppShortcutCommandId) => Promise<boolean>
  getShortcutLabel: (commandId: AppShortcutCommandId) => string
  commands: ShortcutCommandDefinition[]
}

const ShortcutContext = createContext<ShortcutContextValue | null>(null)

const canUseCommand = (
  command: ShortcutCommandDefinition,
  role: UserRole | null,
  isAuthenticated: boolean,
  hasPermission: (resource: PermissionResource, action: PermissionAction) => boolean,
) => {
  if (command.requiresAuth && !isAuthenticated) {
    return false
  }

  if (command.allowedRoles?.length) {
    if (!role || !command.allowedRoles.includes(role)) {
      return false
    }
  }

  if (command.permission && !hasPermission(command.permission.resource, command.permission.action)) {
    return false
  }

  return true
}

export const ShortcutProvider = observer(({ children }: { children: ReactNode }) => {
  const router = useRouter()
  const pathname = usePathname()
  const { authStore } = useStore()

  const [platform, setPlatform] = useState<ShortcutPlatform>("windows")
  const [isShortcutsModalOpen, setIsShortcutsModalOpen] = useState(false)

  useEffect(() => {
    setPlatform(getShortcutPlatform())
  }, [])

  const commands = useMemo(
    () =>
      SHORTCUT_COMMAND_DEFINITIONS.filter((command) =>
        canUseCommand(command, authStore.role, authStore.isAuthenticated, authStore.hasPermission.bind(authStore)),
      ),
    [authStore, authStore.isAuthenticated, authStore.role, authStore.permissions],
  )

  const commandsByShortcut = useMemo(() => {
    const byShortcut = new Map<string, ShortcutCommandDefinition>()
    for (const command of commands) {
      byShortcut.set(command.shortcut, command)
    }
    return byShortcut
  }, [commands])

  const executeCommand = useCallback(
    async (commandId: AppShortcutCommandId) => {
      const command = SHORTCUT_COMMANDS_BY_ID[commandId]
      if (
        !command ||
        !canUseCommand(command, authStore.role, authStore.isAuthenticated, authStore.hasPermission.bind(authStore))
      ) {
        return false
      }

      if (command.id === APP_SHORTCUT_COMMANDS.authLogout) {
        await authStore.logout()
        router.replace("/")
        return true
      }

      if (command.id === APP_SHORTCUT_COMMANDS.shortcutsOpen) {
        setIsShortcutsModalOpen((prev) => !prev)
        return true
      }

      if (command.id === APP_SHORTCUT_COMMANDS.searchFocus) {
        const searchInput = document.querySelector<HTMLInputElement>("[data-shortcut-search-input='true']")
        if (searchInput) {
          searchInput.focus()
          searchInput.select()
          return true
        }
      }

      if (command.id === APP_SHORTCUT_COMMANDS.profileOpen) {
        router.push(getProfilePath(authStore.user?.role))
        return true
      }

      if (command.path) {
        router.push(command.path)
        return true
      }

      return false
    },
    [authStore, router],
  )

  const getShortcutLabel = useCallback(
    (commandId: AppShortcutCommandId) => {
      const command = SHORTCUT_COMMANDS_BY_ID[commandId]
      if (!command) {
        return ""
      }
      return formatShortcutLabel(command.shortcut, platform)
    },
    [platform],
  )

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.repeat || isEventTargetEditable(event.target)) {
        return
      }

      for (const [shortcut, command] of commandsByShortcut.entries()) {
        if (!doesShortcutMatch(event, shortcut, platform)) {
          continue
        }

        if (command.path && pathname === command.path) {
          continue
        }

        event.preventDefault()
        void executeCommand(command.id)
        return
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [commandsByShortcut, executeCommand, pathname, platform])

  const value = useMemo<ShortcutContextValue>(
    () => ({
      executeCommand,
      getShortcutLabel,
      commands,
    }),
    [commands, executeCommand, getShortcutLabel],
  )

  return (
    <ShortcutContext.Provider value={value}>
      {children}
      <KeyboardShortcutsModal
        open={isShortcutsModalOpen}
        onOpenChange={setIsShortcutsModalOpen}
        commands={commands}
        getShortcutLabel={getShortcutLabel}
      />
    </ShortcutContext.Provider>
  )
})

export const useShortcutCommands = () => {
  const context = useContext(ShortcutContext)
  if (!context) {
    throw new Error("useShortcutCommands must be used within ShortcutProvider")
  }
  return context
}

